import { inject, Injectable } from '@angular/core';
import { LocalStorageService } from './local.storage.service';
import { environment } from '@shared-app-config';
import { finalize, Observable, of, share, tap } from 'rxjs';
import { CommonProfileApiService, PlatformType, ProfileWebModel } from '@data-access';
import { MatDialog } from '@angular/material/dialog';
import { CABINET_TYPE } from '../constants/platform-type.constants';
import { SessionLifecycle } from './session-lifecycle.service';
import { SessionStore } from '../session/session.store';
import { toSyncObservable } from '../_utils/to-sync-observable';

@Injectable({ providedIn: 'root' })
export class UserService {
  private commonProfileApiService = inject(CommonProfileApiService);
  private lsService = inject(LocalStorageService);
  private dialog = inject(MatDialog);

  cabinetType = inject(CABINET_TYPE);
  private sessionLifecycle = inject(SessionLifecycle);

  private readonly session = inject(SessionStore);
  private readonly profile$ = toSyncObservable(this.session.profile);

  readonly currentUser = this.session.profile;
  readonly currentCarrierId = this.session.currentCarrierId;
  readonly currentCustomerId = this.session.currentCustomerId;

  /** Guards and resolvers ask at once on start-up; they share the request that is in flight. */
  private profileRequest: Observable<ProfileWebModel> | undefined;

  getProfile(): Observable<ProfileWebModel> {
    const cached = this.currentUser();
    if (cached) return of(cached);
    return (this.profileRequest ??= this.commonProfileApiService.getOwnProfile().pipe(
      finalize(() => (this.profileRequest = undefined)),
      share(),
    ));
  }

  clearProfile() {
    this.session.clearProfile();
  }

  logOut() {
    return this.commonProfileApiService.logout().pipe(
      tap(() => {
        this.dialog.closeAll();
        this.sessionLifecycle.reset();

        this.lsService.remove('accessToken');
        this.lsService.remove('refreshToken');
        this.lsService.remove('deviceId');
        this.lsService.remove('expiresIn');

        if (this.cabinetType === PlatformType.WebAdmin) {
          window.location.href = environment.frontendUrl.admin;
        } else {
          window.location.href = environment.frontendUrl.login;
        }
      }),
    );
  }

  setCabinetAccess(profile: ProfileWebModel) {
    this.session.setProfile(profile);
  }

  selectUserProfile$() {
    return this.profile$;
  }

  updateUserProfile(profile: ProfileWebModel) {
    this.session.updateProfile(profile);
  }
}
